// Serverless S3 service using Netlify functions (no frontend AWS credentials needed)
export interface S3UploadResult {
  success: boolean;
  url?: string;
  key?: string;
  error?: string;
}

export interface S3UploadOptions {
  folder: 'previews' | 'originals';
  contentType?: string;
  isPublic?: boolean;
}

// Convert file to base64 for Netlify function
const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      const result = reader.result as string;
      // Remove data URL prefix (data:image/jpeg;base64,)
      const base64 = result.split(',')[1];
      resolve(base64);
    };
    reader.onerror = (error) => reject(error);
  });
};

// Upload file to S3 via Netlify function
export const uploadToS3 = async (
  file: File,
  fileName: string,
  options: S3UploadOptions
): Promise<S3UploadResult> => {
  try {
    console.log(`Converting file to base64 for serverless upload: ${fileName}`);
    const fileData = await fileToBase64(file);
    
    const response = await fetch('/.netlify/functions/upload-to-s3', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        fileName,
        folder: options.folder,
        contentType: options.contentType || file.type,
        fileData
      })
    });

    if (!response.ok) {
      throw new Error(`Upload failed: ${response.status} ${response.statusText}`);
    }

    const result = await response.json();
    console.log(`Serverless upload result for ${fileName}:`, result);
    
    return result;
  } catch (error) {
    console.error('Serverless S3 upload error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Upload failed'
    };
  }
};

// Generate signed URL for secure downloads via Netlify function
export const generateSignedUrl = async (
  key: string,
  expiresIn: number = 3600
): Promise<{ success: boolean; url?: string; error?: string }> => {
  try {
    const response = await fetch('/.netlify/functions/get-download-url', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        key,
        expiresIn
      })
    });

    if (!response.ok) {
      throw new Error(`Failed to generate signed URL: ${response.status} ${response.statusText}`);
    }

    const result = await response.json();
    return result;
  } catch (error) {
    console.error('Error generating signed URL via serverless:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to generate download link'
    };
  }
};

// Delete file from S3 via Netlify function
export const deleteFromS3 = async (key: string): Promise<boolean> => {
  try {
    const response = await fetch('/.netlify/functions/delete-from-s3', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ key })
    });

    if (!response.ok) {
      throw new Error(`Delete failed: ${response.status} ${response.statusText}`);
    }

    const result = await response.json();
    console.log(`Serverless delete result for ${key}:`, result);
    
    return result.success;
  } catch (error) {
    console.error('Serverless S3 delete error:', error);
    return false;
  }
};

// Get file info from S3 via Netlify function
export const getS3FileInfo = async (key: string): Promise<{
  success: boolean;
  size?: number;
  lastModified?: Date;
  error?: string;
}> => {
  try {
    const response = await fetch('/.netlify/functions/get-s3-file-info', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ key })
    });

    if (!response.ok) {
      throw new Error(`Failed to get file info: ${response.status} ${response.statusText}`);
    }

    const result = await response.json();
    return result;
  } catch (error) {
    console.error('Error getting S3 file info via serverless:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to get file info'
    };
  }
};

// Check if serverless S3 is available (always true since we use Netlify functions)
export const isAWSConfigured = (): boolean => {
  return true; // Netlify functions handle AWS credentials
};

// Get S3 configuration status (simplified for serverless)
export const getAWSStatus = () => {
  return {
    configured: true,
    region: 'eu-north-1', // Your configured region
    bucket: 'algranoconpablo-galleries', // Your bucket name
    hasCredentials: true // Netlify functions handle credentials
  };
};